<template>
  <div
    class="w-full max-w-[388px] border rounded-md p-[17px] pt-3 font-secondary text-[16px] leading-[22px] text-gray-800"
    :class="classes"
  >
    <div class="font-semibold">{{ title }}</div>
    <div class="font-medium opacity-65">{{ description }}</div>
    <UiButton
      v-if="showButton"
      class="w-full mt-[13px]"
      :primary="!fileLimitReached"
      :danger="fileLimitReached"
      @click="handleClick"
    >
      {{ buttonCopy }}
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import { PlanType, UserPermission } from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { UiButton } from '@dacr/ui'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const router = useRouter()
const dialog = useDialog()
const userStore = useUserStore()
const billingStore = useBillingStore()

const isBasicAccount = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const hasBundlePlan = computed(
  () => billingStore.billing?.plan.type === PlanType.Bundle
)

const showButton = computed(
  () =>
    userStore.user?.canUpgradePlan &&
    (isBasicAccount.value || hasBundlePlan.value)
)

const fileLimitReached = computed(
  () =>
    !!(
      userStore.user?.totalFilesLimit &&
      (userStore.user?.totalFiles || 0) >= userStore.user.totalFilesLimit
    )
)

const classes = computed(() => ({
  ...(showButton.value && {
    'border-red-700/25 bg-red-200/50': fileLimitReached.value,
    'border-green-700/25 bg-green-200/50': !fileLimitReached.value,
  }),
  'border-gray-300/25 bg-gray-200/25 ': !showButton.value,
}))

const title = computed(() => {
  if (fileLimitReached.value) return 'Copyright limit reached'
  if (isBasicAccount.value) {
    return `${userStore.user?.totalFiles} of ${userStore.user?.totalFilesLimit} copyrights added`
  }
  return `${billingStore.billing?.plan.name} plan`
})

const description = computed(() => {
  if (
    fileLimitReached.value &&
    (billingStore.billing?.plan.type === PlanType.Basic ||
      !userStore.user?.permissions.includes(UserPermission.Pro))
  ) {
    return 'Upgrade to Pro to add unlimited copyrights'
  }
  if (
    fileLimitReached.value &&
    billingStore.billing?.plan.type === PlanType.Bundle
  ) {
    return 'Upgrade your plan to add more copyrights'
  }
  return billingStore.billing?.plan.description
})

const buttonCopy = computed(() => {
  if (hasBundlePlan.value) return 'Upgrade your limit'
  return 'See plans'
})

const handleClick = () => {
  if (hasBundlePlan.value) {
    return dialog.open(Dialogs.UpgradeBundleLimit, {
      props: { hasReachedLimit: fileLimitReached.value },
    })
  }
  emit('close')
  return router.push('/upgrade')
}
</script>
